const ABORT_ERROR = "AbortError";
const USER_ABORT_MESSAGE = "Request cancelled by user";
const TIMEOUT_MESSAGE = "Request timeout";

interface FetchOptions extends RequestInit {
  abortSignal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * Creates a timeout AbortController that automatically aborts after the specified duration
 */
const createTimeoutController = (timeoutMs: number): AbortController => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(TIMEOUT_MESSAGE);
  }, timeoutMs);

  // Store the timeoutId on the controller for cleanup
  (controller as any).timeoutId = timeoutId;
  return controller;
};

/**
 * Cleanup function to clear timeout and remove abort listeners
 */
const cleanup = (timeoutController: AbortController) => {
  const timeoutId = (timeoutController as any).timeoutId;
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
};

/**
 * Enhanced fetch implementation with abort and timeout support
 */
const fetchWithAbort = async (
  url: string,
  options: FetchOptions = {}
): Promise<Response> => {
  const { abortSignal, timeoutMs = 40000, ...fetchOptions } = options;

  // Validate abort signal
  if (!abortSignal) {
    throw new Error("AbortSignal is required");
  }

  // Create timeout controller
  const timeoutController = createTimeoutController(timeoutMs);

  try {
    // Create a composite signal that aborts if either the user aborts or timeout occurs
    const commonController = new AbortController();

    // Listen to user's abort signal
    abortSignal.addEventListener(
      "abort",
      () => {
        cleanup(timeoutController);
        commonController.abort(USER_ABORT_MESSAGE);
      },
      { once: true }
    );

    // Listen to timeout signal
    timeoutController.signal.addEventListener(
      "abort",
      () => {
        commonController.abort(TIMEOUT_MESSAGE);
      },
      { once: true }
    );

    const response = await fetch(url, {
      ...fetchOptions,
      signal: commonController.signal,
    });

    // Cleanup after successful response
    cleanup(timeoutController);
    return response;
  } catch (error: any) {
    cleanup(timeoutController);

    if (error.name === ABORT_ERROR) {
      const isTimeout = error.message === TIMEOUT_MESSAGE;
      const timeoutBody = JSON.stringify({
        error: isTimeout ? TIMEOUT_MESSAGE : USER_ABORT_MESSAGE,
        fullUrl:
          typeof window !== "undefined" ? `${location.href}/${url}` : url,
      });

      return new Response(timeoutBody, {
        status: isTimeout ? 408 : 499, // HTTP 408 is Request Timeout, 499 is commonly used for Client Cancellation
        statusText: isTimeout ? "Request Timeout" : "Client Closed Request",
      });
    }

    // Re-throw other errors
    throw error;
  }
};

export default fetchWithAbort;
